import _ from 'lodash';

import {
  firebaseAddDoc,
  firebaseDeleteDoc,
  firebaseGetAll,
  firebaseGetBy,
  firebaseGetById,
  firebaseGetDocRef,
  firebaseUpdateDoc,
} from '@client/api/firebase.api';
import {
  cleanData,
  createMetadata,
  getAuthUser,
  prepareMetadataUpdate,
  updateMetadata,
} from '@client/services/common.service';
import { firebaseAuth } from '@client/services/firebase.config';
import { isNewMode } from '@client/utils/form.util';
import { logger } from '@client/utils/logger.util';
import { getTime } from '@client/utils/metadata.util';
import {
  CFeaturesPath,
  EErrorType,
  type EFeatureType,
  ESort,
  EStatus,
  type IFeature,
} from '@common/types';

import type { TPaginateProps } from '@client/types/antd.type';
import type { TablePaginationConfig } from 'antd/es/table/interface';
import type { User } from 'firebase/auth';
import type { DocumentData } from 'firebase/firestore';

export const getFeatureRefs = async (features: IFeature[]) =>
  Promise.all(
    features.map(async (feature) =>
      firebaseGetDocRef({
        collectionPath: CFeaturesPath,
        document: feature.id,
      }),
    ),
  );

export const getFeature = async (
  id: Required<IFeature['id']>,
): Promise<IFeature | null> => {
  try {
    if (isNewMode(id)) return null;
    const feature = await firebaseGetById({
      collectionPath: CFeaturesPath,
      document: id as string,
    });
    return feature as IFeature;
  } catch (error) {
    logger.error('getFeature', error);
    throw error;
  }
};

export const createFeature = async (data: IFeature) => {
  const user = firebaseAuth?.currentUser;
  if (!user) throw new Error(EErrorType.NOT_AUTHENTICATED);
  return firebaseAddDoc<IFeature>({
    collectionPath: CFeaturesPath,
    data: {
      ...cleanData(data),
      status: EStatus.PROCESSING,
      metadata: createMetadata(user),
    },
  });
};

export const updateFeature = async (
  id: Required<IFeature['id']>,
  data: IFeature,
) => {
  try {
    const user = getAuthUser();

    const existing = await firebaseGetById({
      collectionPath: CFeaturesPath,
      document: id,
    });

    let existingMetadata = {};
    if (existing) {
      existingMetadata = existing.metadata ?? {};
    }

    return firebaseUpdateDoc<IFeature>({
      collectionPath: CFeaturesPath,
      document: id as string,
      data: {
        ...cleanData(data),
        metadata: {
          ...existingMetadata,
          ...updateMetadata(user as User),
        },
      },
    });
  } catch (error) {
    logger.error('updateFeature', error);
    throw error;
  }
};

export const deleteFeature = async (id: Required<IFeature['id']>) =>
  firebaseDeleteDoc({
    collectionPath: CFeaturesPath,
    document: id as string,
  });

export const deactivateFeature = async (id: Required<IFeature['id']>) => {
  await firebaseUpdateDoc({
    collectionPath: CFeaturesPath,
    document: id as string,
    data: {
      status: EStatus.DEACTIVATED,
    },
  });

  return getUserFeatures();
};

export const getFeatureBySlug = async (
  slug: string,
): Promise<IFeature | null> => {
  const user = getAuthUser();
  try {
    const result = await firebaseGetBy<IFeature>({
      collectionPath: CFeaturesPath,
      wheres: [
        { field: 'metadata.createdBy.uid', value: user.uid },
        { field: 'slug', value: slug },
      ],
      optional: { limit: 1 },
    });
    if (!result?.length) return null;
    const doc = result[0] as IFeature & { id: string };
    return _.merge({ id: doc.id }, doc) as IFeature;
  } catch (error) {
    logger.error('getFeatureBySlug', error);
    throw error;
  }
};

export const togglePublic = async (feature: IFeature) =>
  firebaseUpdateDoc<IFeature>({
    collectionPath: CFeaturesPath,
    document: feature.id as string,
    data: {
      isPublic: !feature.isPublic,
      ...prepareMetadataUpdate<IFeature>(feature),
    },
  });

export const getAllFeatures = async (
  params?: TPaginateProps & TablePaginationConfig,
) => {
  logger.debug('params', params);

  try {
    const features = await firebaseGetAll<IFeature & { id: string }>({
      collectionPath: CFeaturesPath,
    });

    const data: IFeature[] = [];
    if ('length' in features) {
      features?.forEach((doc: IFeature & { id: string }) => {
        data.push(_.merge(doc, { id: doc.id }) as IFeature);
      });
    }

    return data;
  } catch (error) {
    logger.error('getFeatures', error);
    throw error;
  }
};

export const getUserFeatures = async (
  params?: TPaginateProps & TablePaginationConfig,
) => {
  const user = getAuthUser();

  try {
    const result =
      (await firebaseGetBy({
        collectionPath: CFeaturesPath,
        wheres: [{ field: 'metadata.createdBy.uid', value: user.uid }],
        optional: {
          order: 'metadata.updatedAt',
          orderDirection: ESort.DESC,
          limit: params?.pageSize,
        },
      })) || [];

    return {
      [CFeaturesPath]: result?.map(
        (doc: DocumentData) => _.merge({ id: doc.id }, doc) as IFeature,
      ),
      total: result?.length,
    };
  } catch (error) {
    logger.error('getFeatures error', error);
    throw error;
  }
};

export const getActiveFeatures = async (
  type: keyof typeof EFeatureType,
  params?: TPaginateProps & TablePaginationConfig,
) => {
  const user = getAuthUser();

  try {
    // Query 1: Own features
    const ownFeatures = await firebaseGetBy({
      collectionPath: CFeaturesPath,
      wheres: [
        { field: 'type', value: type },
        { field: 'metadata.createdBy.uid', value: user.uid },
      ],
      optional: {
        order: 'metadata.updatedAt',
        orderDirection: ESort.DESC,
        limit: params?.pageSize,
      },
    });

    // Query 2: Public features by others
    const publicFeatures = await firebaseGetBy({
      collectionPath: CFeaturesPath,
      wheres: [
        { field: 'type', value: type },
        { field: 'isPublic', value: true },
        { field: 'status', value: 'ACTIVE' },
      ],
      optional: {
        order: 'metadata.updatedAt',
        orderDirection: ESort.DESC,
        limit: params?.pageSize,
      },
    });

    // Filter out duplicates (in case the user's own feature is also public)
    const publicFiltered = publicFeatures.filter(
      (feature) => (feature as IFeature)?.metadata?.createdBy?.uid !== user.uid,
    );

    // Merge and optionally sort again
    const combined = [...ownFeatures, ...publicFiltered];

    // You may want to sort again to maintain a consistent order
    combined.sort(
      (a, b) =>
        getTime((b as IFeature).metadata) - getTime((a as IFeature).metadata),
    );

    return {
      [CFeaturesPath]: combined.map(
        (doc: DocumentData) => _.merge({ id: doc.id }, doc) as IFeature,
      ),
      total: combined.length,
    };
  } catch (error) {
    logger.error('getActiveFeatures error', error);
    throw error;
  }
};

export const toggleFeatureStatus = async (feature: IFeature) =>
  firebaseUpdateDoc<IFeature>({
    collectionPath: CFeaturesPath,
    document: feature.id as string,
    data: {
      status:
        feature.status === EStatus.ACTIVE
          ? EStatus.DEACTIVATED
          : EStatus.ACTIVE,
      ...prepareMetadataUpdate<IFeature>(feature),
    },
  });
